import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { ToastType } from '@components/Toast/Toast';
import { setLoadBoardStore } from '@store/loadboard/store';
import {
  createPostLoad,
  deletePosting,
  fetchPosting,
  postPosting,
  updatePostLoad,
} from '@store/orders/services/postLoadServices';
import {
  setManagePostLoadData,
  setPostLoadData,
  setPostLoadStore,
} from '@store/orders/store';
import { IManagePostLoad, IPostLoad } from '@store/orders/types';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';

import { updateLoadPropertyAtIndex } from './orderFormActions';
import { mapEditPostLoad } from '../mapper';

export const quickPostLoad = async (
  loadId: number,
  postingId: number,
  loadNumber?: string,
) => {
  try {
    setLoadBoardStore('overlay', true);
    if (postingId) {
      const deleteResponse = await deletePosting(postingId);
      if (deleteResponse.isSuccess) {
        setLoadBoardStore(
          'items',
          (items) => items.loadId === loadId,
          'postingId',
          0,
        );
      }
    } else {
      const postingResponse = await postPosting(loadId, loadNumber);
      if (Boolean(postingResponse)) {
        setLoadBoardStore(
          'items',
          (items) => items.loadId === loadId,
          'postingId',
          postingResponse,
        );
      }
    }
    setLoadBoardStore('overlay', false);
  } catch (error) {
    handleToast(ToastType.Error, (error as Error).message);
    setLoadBoardStore('overlay', false);
  }
};

export const savePostLoad = async (formValues: IPostLoad) => {
  try {
    const response = await createPostLoad(
      formValues,
      false,
      CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsPostLoad,
    );
    if (response) {
      await fetchPostLoadByPostingId(response.id);
      setPostLoadData(response);
      updateLoadPropertyAtIndex({ postingId: response.id });
    }
  } catch (error) {
    logError('savePostLoad', error as Error);
    setPostLoadStore(() => ({
      isError: true,
    }));
  } finally {
    setPostLoadStore(() => ({
      loading: false,
    }));
  }
};

export const editPostLoad = async (formValues: IManagePostLoad) => {
  try {
    const response = await updatePostLoad(
      mapEditPostLoad(formValues),
      false,
      CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsPostLoad,
    );
    setManagePostLoadData(response);
  } catch (error) {
    logError('editPostLoad', error as Error);
    setPostLoadStore(() => ({
      isError: true,
    }));

    if (error instanceof Error) {
      throw error;
    }

    throw createError(error, 'Edit post load');
  } finally {
    setPostLoadStore(() => ({
      loading: false,
    }));
  }
};

export const fetchPostLoadByPostingId = async (postingId: number | null) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- postingId is called with undefined at some call sites despite its number | null type
  if (postingId === null || postingId === undefined) {
    setPostLoadStore(() => ({
      loading: false,
    }));
    return;
  }

  try {
    const response = await fetchPosting(postingId);
    setManagePostLoadData(response);
  } catch (error) {
    logError('fetchPostLoadByPostingId', error as Error);
    setPostLoadStore(() => ({
      isError: true,
    }));
  } finally {
    setPostLoadStore(() => ({
      loading: false,
    }));
  }
};
